import { ref } from 'vue';
import { PAGINATION } from '~helpers/constants';

/**
 * A composable function for pagination management.
 *
 * @returns An object containing:
 *   - `pagination` - Reactive pagination state object
 *   - `CallSetPagination` - Function to set pagination values
 *   - `CallPaginate` - Function to update current page and page size, returns a function to execute a callback
 *   - `CallReset` - Function to reset pagination to default values
 *
 * @example
 * ```
 * const { pagination, CallSetPagination, CallPaginate, CallReset } = usePaginator();
 *
 * // Set custom pagination values
 * CallSetPagination({ currentPage: 2, pageSize: 20, firstItem: 20, resultCount: 100 });
 *
 * // Handle pagination change from UI component
 * const handlePageChange = async ({ currentPage, rows }) => {
 *   const paginate = await CallPaginate({ currentPage, rows });
 *   await paginate(() => fetchData());
 * };
 *
 * // Reset pagination to defaults
 * CallReset();
 * ```
 */
function usePaginator() {
  const pagination = ref({ ...PAGINATION });

  /**
   * Updates the first item index in the pagination based on the current page and page size.
   * The first item is calculated as (currentPage - 1) * pageSize, representing the zero-based index
   * of the first item displayed on the current page.
   */
  function updateFirstItem() {
    pagination.value.firstItem = (pagination.value.currentPage - 1) * pagination.value.pageSize;
  }

  /**
   * Updates the pagination state with the provided values.
   * After setting the new pagination values, it updates the first item position.
   *
   * @param values - The pagination values to set
   */
  function setPagination(values: any) {
    pagination.value = { ...values };
    updateFirstItem();
  }

  /**
   * Updates the pagination state and returns a function that allows for asynchronous callback execution.
   * @param {Object} options - The pagination options
   * @param {number} options.currentPage - Zero-based page index (will be converted to one-based)
   * @param {number} options.rows - Number of rows per page
   * @returns {Function} - A function that accepts and executes an asynchronous callback
   * @example
   * const paginateAndFetch = await paginate({ currentPage: 0, rows: 10 });
   * await paginateAndFetch(fetchData);
   */
  async function paginate({ currentPage, rows }: any) {
    pagination.value.currentPage = currentPage + 1;
    pagination.value.pageSize = rows;

    return async function (callback: (args?: any) => any) {
      await callback();
    };
  }

  /**
   * Updates pagination values based on the provided currentPage information and executes a callback function.
   *
   * @param options - The pagination options
   * @param options.currentPage - Zero-based index of the current page
   * @param options.rows - Number of rows per currentPage
   * @param callback - Async function to execute after updating pagination values
   * @returns Promise that resolves to the result of the callback function
   */
  async function paginateAndFetch(
    { currentPage, rows, page }: any,
    callback: (args?: any) => Promise<any>,
  ) {

    pagination.value.currentPage = page + 1 || currentPage + 1;
    pagination.value.pageSize = rows;

    return await callback();
  }

  /**
   * Resets the pagination state to its initial values.
   * This function sets the pagination value back to the default PAGINATION constant.
   * @returns void
   */
  function reset() {
    pagination.value = { ...PAGINATION };
  }

  return {
    pagination,
    CallSetPagination: setPagination,
    CallPaginate: paginate,
    CallResetPagination: reset,
    CallPaginateAndFetch: paginateAndFetch,
  };
}

export default usePaginator;
export { usePaginator };
